import { useEffect, useRef } from 'react'
import type { ToastId } from '@chakra-ui/react'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/useToast'

export function AuthStatusNotification() {
  const { isAuthenticated, isLoading, isTokenRefreshing, tokenRefreshError } = useAuth()
  const { loading, warning, error, close } = useToast()
  const refreshToastId = useRef<ToastId | null>(null)
  const errorToastId = useRef<ToastId | null>(null)
  const notifiedError = useRef<string | null>(null)

  // Display unified loading toast during token refresh
  useEffect(() => {
    if (isAuthenticated && isTokenRefreshing) {
      refreshToastId.current = loading({
        id: 'token-refresh',
        titleKey: 'auth.refreshingToken',
        descriptionKey: 'auth.refreshingTokenDescription',
        duration: null,
        closeOnClick: false,
        position: 'top-right',
      })
      return
    }

    if (refreshToastId.current) {
      close(refreshToastId.current)
      refreshToastId.current = null
    }
  }, [isAuthenticated, isTokenRefreshing, loading, close])

  useEffect(() => {
    if (!tokenRefreshError) {
      notifiedError.current = null
      if (errorToastId.current) {
        close(errorToastId.current)
        errorToastId.current = null
      }
      return
    }
    if (isLoading) return

    const notification = `${isAuthenticated}:${tokenRefreshError}`
    // A dismissed failure stays acknowledged until the authentication error changes.
    if (notifiedError.current === notification) return
    notifiedError.current = notification
    if (!isAuthenticated) {
      errorToastId.current = warning({
        id: 'token-refresh-error',
        descriptionKey: 'auth.sessionVerificationUnavailable',
        duration: 6000,
        position: 'top-right',
        closeOnClick: true,
      })
    } else {
      const isSessionExpired = tokenRefreshError.toLowerCase().includes('expired')
      errorToastId.current = error({
        id: 'token-refresh-error',
        titleKey: isSessionExpired ? 'auth.sessionExpiring' : 'auth.connectionIssue',
        descriptionKey: isSessionExpired
          ? 'auth.sessionExpiringDescription'
          : 'auth.connectionIssueDescription',
        duration: 6000,
        position: 'top-right',
        closeOnClick: true,
      })
    }
  }, [isAuthenticated, isLoading, tokenRefreshError, warning, error, close])

  useEffect(() => {
    return () => {
      if (refreshToastId.current) {
        close(refreshToastId.current)
        refreshToastId.current = null
      }
      // Finite error toasts survive StrictMode effect replay; state changes close them above.
    }
  }, [close])

  return null
}
